import mapbox from "mapbox-gl";
import getMapboxRouteLayer from "@/helpers/getMapboxRouteLayer";
import cssVariables from "@/config/cssVariables"
import mapboxConfig from "@/config/mapbox";

/**
 * Generate a data URL representing a map route.
 *
 * @param {Route} route - The route object containing coordinates.
 * @param {Number} width - The width of the map container.
 * @param {Number} height - The height of the map container.
 * @param {Number} improvementMultiplier - Map quality improvement multiplier
 *
 * @return {Promise<string>} - A promise that resolves to the data URL of the map route.
 */
export default async function generateMapRouteDataURL(route, width, height, improvementMultiplier = 1) {
  const geometry = await route.getGeometry()

  return new Promise((resolve) => {
    const mapContainer = document.createElement('div')

    const improvementWidth = improvementMultiplier * width
    const improvementHeight = improvementMultiplier * height

    mapContainer.style.width = `${ improvementWidth }px`
    mapContainer.style.height = `${ improvementHeight }px`

    document.body.appendChild(mapContainer)

    const geometryCoordinates = geometry.coordinates

    let minLng = geometryCoordinates[0][0]
    let maxLng = geometryCoordinates[0][0]
    let minLat = geometryCoordinates[0][1]
    let maxLat = geometryCoordinates[0][1]

    geometryCoordinates.forEach(([lng, lat]) => {
      minLng = Math.min(minLng, lng)
      maxLng = Math.max(maxLng, lng)
      minLat = Math.min(minLat, lat)
      maxLat = Math.max(maxLat, lat)
    })

    const lngPadding = (maxLng - minLng) * .1
    const latPadding = (maxLat - minLat) * .1

    const map = new mapbox.Map({
      container: mapContainer,
      style: 'mapbox://styles/mapbox/streets-v11',
      zoom: 15,
      bounds: [
        [minLng - lngPadding, minLat - latPadding],
        [maxLng + lngPadding, maxLat + latPadding]
      ]
    })

    map.on('load', async () => {
      const geometryLayer = getMapboxRouteLayer(geometry, mapboxConfig.defaultMapRouteLineWidth * improvementMultiplier)
      map.addLayer(geometryLayer)

      map.addSource('coordinates', {
        'type': 'geojson',
        'data': {
          'type': 'FeatureCollection',
          'features': route.coordinates.map((coordinate, index) => {
            return {
              'type': 'Feature',
              'properties': {
                'number': index + 1
              },
              'geometry': {
                'type': 'Point',
                'coordinates': [coordinate.lng, coordinate.lat]
              }
            }
          })
        }
      })

      map.addLayer({
        'id': 'maine',
        'type': 'circle',
        'source': 'coordinates',
        'layout': {
          'visibility': 'visible'
        },
        'paint': {
          'circle-radius': 25 * improvementMultiplier,
          'circle-color': 'white',
          'circle-stroke-width': 3 * improvementMultiplier,
          'circle-stroke-color': cssVariables["app-primary-color"]
        }
      })

      map.addLayer({
        'id': 'off-leash-areas',
        'type': 'symbol',
        'source': 'coordinates',
        'layout': {
            'text-field': [
            "format",
            ['get', 'number'],
            { 'text-color': cssVariables["app-primary-color"] }
          ],
          'text-size': 32 * improvementMultiplier
        }
      })

      map.on('idle', () => {
        const canvas = map._canvas
        const dataURL = canvas.toDataURL()

        map.remove()
        mapContainer.remove()

        resolve(dataURL)
      })
    })
  })
}